import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { apiFetch } from "@pontimed/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";

import { getPortalUrl } from "@/lib/portal-url";

export type DbTarget = "local" | "prod";

interface DbInfo {
  host?: string;
  label: string;
  available: boolean;
}

interface EnvironmentResponse {
  environment: "vercel" | "local";
  scrapingEnabled: boolean;
  /** False where no worker drains the scraping queue (Fly): runs go from the Mac. Absent = older API. */
  scrapingRunner?: boolean;
  maxPerCityLabel: string;
  skipExisting: boolean;
  localDatabase: { host: string; label: string };
  prodDatabase: DbInfo;
}

export interface CountryOption {
  code: string;
  name: Record<string, string>;
  flagEmoji: string | null;
}

interface AdminContextValue {
  dbTarget: DbTarget;
  setDbTarget: (t: DbTarget) => void;
  localDb: DbInfo | null;
  prodDb: DbInfo | null;
  environment: "vercel" | "local";
  envLoaded: boolean;
  /** Whether this API runs scraping jobs; false on the deployed admin. */
  scrapingRunner: boolean;
  prodCooldown: boolean;
  country: string | null;
  setCountry: (code: string | null) => void;
  countries: CountryOption[];
  countriesLoaded: boolean;
}

const AdminContext = createContext<AdminContextValue>({
  dbTarget: "local",
  setDbTarget: () => {},
  localDb: null,
  prodDb: null,
  environment: "local",
  envLoaded: false,
  scrapingRunner: true,
  prodCooldown: false,
  country: null,
  setCountry: () => {},
  countries: [],
  countriesLoaded: false,
});

export function useAdminContext() {
  return useContext(AdminContext);
}

const PROD_COOLDOWN_SECONDS = 5;
const DB_STORAGE_KEY = "pontimed_admin_db";
const COUNTRY_STORAGE_KEY = "pontimed_admin_country";

interface CountryRouteSearch {
  country?: string;
}

export function AdminContextProvider({ children }: { children: ReactNode }) {
  const routerState = useRouterState();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const urlCountry =
    typeof (routerState.location.search as CountryRouteSearch).country === "string"
      ? (routerState.location.search as CountryRouteSearch).country!
      : null;

  const [dbTarget, setDbTargetState] = useState<DbTarget>(() => {
    if (typeof window === "undefined") return "local";
    return (sessionStorage.getItem(DB_STORAGE_KEY) as DbTarget) || "local";
  });
  const [country, setCountryState] = useState<string | null>(() => {
    if (urlCountry) return urlCountry;
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem(COUNTRY_STORAGE_KEY);
  });

  const [localDb, setLocalDb] = useState<DbInfo | null>(null);
  const [prodDb, setProdDb] = useState<DbInfo | null>(null);
  const [environment, setEnvironment] = useState<"vercel" | "local">("local");
  const [envLoaded, setEnvLoaded] = useState(false);
  const [scrapingRunner, setScrapingRunner] = useState(true);
  const [prodCooldown, setProdCooldown] = useState(false);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [countriesLoaded, setCountriesLoaded] = useState(false);

  const dbTargetRef = useRef(dbTarget);
  useEffect(() => {
    dbTargetRef.current = dbTarget;
  }, [dbTarget]);

  // ─── Sync country from URL → state when URL changes (back/forward) ───
  useEffect(() => {
    if (urlCountry !== country) {
      setCountryState(urlCountry);
      if (typeof window !== "undefined") {
        if (urlCountry) sessionStorage.setItem(COUNTRY_STORAGE_KEY, urlCountry);
        else sessionStorage.removeItem(COUNTRY_STORAGE_KEY);
      }
    }
  }, [urlCountry, country]);

  // ─── DB target setter (with prod cooldown + confirmation handled by caller) ───
  // On every switch we also invalidate every React Query cache so any page using
  // `useQuery` re-fetches against the new DB. Components that still rely on raw
  // `useEffect(..., [adminFetch])` pick up the change via `useAdminFetch()` —
  // the hook's memoized identity flips on every `dbTarget` mutation.
  const setDbTarget = useCallback(
    (t: DbTarget) => {
      setDbTargetState(t);
      if (typeof window !== "undefined") sessionStorage.setItem(DB_STORAGE_KEY, t);
      if (t === "prod") {
        setProdCooldown(true);
        if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = setTimeout(
          () => setProdCooldown(false),
          PROD_COOLDOWN_SECONDS * 1000,
        );
      } else {
        setProdCooldown(false);
        if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      }
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  // ─── Country setter — updates URL via TanStack navigate + sessionStorage ───
  // Mirrors `setDbTarget`: invalidates every React Query cache so any page
  // using `useQuery` refetches against the new country scope. Pages whose
  // queryKey already includes `country` get re-fetched naturally; the
  // universal invalidate is a safety net for queries that forgot to add
  // `country` to their key. Manual `useEffect` fetches still need `country`
  // (or `selectedCountry` derived from context) in their dep array.
  const setCountry = useCallback(
    (code: string | null) => {
      setCountryState(code);
      if (typeof window !== "undefined") {
        if (code) sessionStorage.setItem(COUNTRY_STORAGE_KEY, code);
        else sessionStorage.removeItem(COUNTRY_STORAGE_KEY);
      }
      // Update URL via TanStack Router so search-param state stays reactive
      // across pages. `to: "."` keeps the current route; we merge the search
      // param without disturbing other route-specific search keys.
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev };
          if (code) next.country = code;
          else delete next.country;
          return next;
        },
        replace: true,
      });
      void queryClient.invalidateQueries();
    },
    [navigate, queryClient],
  );

  // ─── Global fetch interceptor: inject X-Admin-DB + handle TOTP 403 ───
  useEffect(() => {
    if (typeof window === "undefined") return;
    const originalFetch = window.fetch;
    let redirectInFlight = false;

    const handle403 = async (response: Response, _requestUrl: string): Promise<Response> => {
      if (response.status !== 403 || redirectInFlight) return response;
      // 2FA gate — apps/api returns { error: "TOTP_REQUIRED" | "TOTP_CHALLENGE_REQUIRED" }
      const cloned = response.clone();
      let errorCode: string | undefined;
      try {
        const parsed = (await cloned.json()) as { error?: string };
        errorCode = parsed?.error;
      } catch {
        return response;
      }
      if (errorCode === "TOTP_CHALLENGE_REQUIRED") {
        redirectInFlight = true;
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        if (window.location.pathname !== "/2fa-challenge") {
          window.location.assign(`/2fa-challenge?next=${next}`);
        }
      } else if (errorCode === "TOTP_REQUIRED") {
        // User isn't enrolled in 2FA. Admin app has no enrol UI — bounce to
        // the portal's enrolment wizard (same target as the portal's own
        // use-mfa-enforcer.ts). After enrolment the user can return here and
        // complete the challenge.
        redirectInFlight = true;
        window.location.assign(
          `${getPortalUrl()}/account/user/settings?tab=security&sub=professional&forceEnroll=1`,
        );
      }
      return response;
    };

    window.fetch = function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : (input as Request).url;

      // Match both same-origin /api/admin/ and apps/api full URL pattern.
      const isAdminApi = /\/api\/admin\//.test(url);
      if (!isAdminApi) return originalFetch.call(window, input, init);

      const target = dbTargetRef.current;
      const method = (init?.method || "GET").toUpperCase();
      const isDestructive = method === "DELETE";

      const exec = (initWithHeaders?: RequestInit) =>
        originalFetch
          .call(window, input, initWithHeaders ?? init)
          .then((res) => handle403(res, url));

      // apps/api requires `X-Admin-Confirm-Delete: true` on admin DELETE
      // handlers regardless of target, and on Fly the default "local" target
      // IS the production database — so sending the header only for the
      // "prod" toggle made every delete fail with 412 (silently, because
      // several call sites use raw() without checking res.ok).
      if (target !== "prod") {
        if (isDestructive) {
          const headers = new Headers(init?.headers);
          headers.set("X-Admin-Confirm-Delete", "true");
          return exec({ ...init, headers });
        }
        return exec();
      }

      // target === "prod" — the only remaining case (the branch above returns
      // for every other target).
      {
        if (isDestructive) {
          const confirmed = window.confirm(
            "⚠️ PRODUCTION DATABASE\n\n" +
              "You are about to perform a DELETE operation on the PRODUCTION database.\n\n" +
              `URL: ${url}\n\n` +
              "This action may be irreversible. Are you sure?",
          );
          if (!confirmed) {
            return Promise.resolve(
              new Response(JSON.stringify({ error: "Cancelled by user" }), {
                status: 499,
                headers: { "Content-Type": "application/json" },
              }),
            );
          }
        }
        const headers = new Headers(init?.headers);
        if (!headers.has("X-Admin-DB")) headers.set("X-Admin-DB", "prod");
        if (isDestructive) headers.set("X-Admin-Confirm-Delete", "true");
        return exec({ ...init, headers });
      }
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // ─── Fetch environment info (db labels, prod availability, vercel detection) ───
  useEffect(() => {
    apiFetch
      .get<EnvironmentResponse>("/api/admin/scraping/environment")
      .then((data) => {
        setLocalDb({
          host: data.localDatabase.host,
          label: data.localDatabase.label,
          available: true,
        });
        setProdDb(data.prodDatabase);
        setEnvironment(data.environment);
        setScrapingRunner(data.scrapingRunner !== false);
        setEnvLoaded(true);
        if (dbTargetRef.current === "prod" && !data.prodDatabase.available) {
          setDbTarget("local");
        }
      })
      .catch(() => {
        // Network or auth failure — degrade gracefully so the toggle still
        // renders in Local-only mode rather than disappearing.
        setLocalDb({ label: "Local DB", available: true });
        setProdDb({ available: false, label: "Unavailable" });
        setEnvironment("local");
        setEnvLoaded(true);
      });
  }, [setDbTarget]);

  // ─── Fetch country list (for selector dropdown) ───
  // Re-runs on DB toggle: the countries table differs between Local and Prod,
  // and the fetch interceptor above stamps X-Admin-DB per request.
  useEffect(() => {
    apiFetch
      .get<CountryOption[] | { items: CountryOption[] }>("/api/admin/countries")
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.items ?? []);
        setCountries(list);
        setCountriesLoaded(true);
      })
      .catch(() => {
        setCountriesLoaded(true);
      });
  }, [dbTarget]);

  const value = useMemo<AdminContextValue>(
    () => ({
      dbTarget,
      setDbTarget,
      localDb,
      prodDb,
      environment,
      envLoaded,
      scrapingRunner,
      prodCooldown,
      country,
      setCountry,
      countries,
      countriesLoaded,
    }),
    [
      dbTarget,
      setDbTarget,
      localDb,
      prodDb,
      environment,
      envLoaded,
      scrapingRunner,
      prodCooldown,
      country,
      setCountry,
      countries,
      countriesLoaded,
    ],
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}
